import { Helmet } from "react-helmet-async";

/**
 * Per-page SEO component. Renders canonical, og, twitter and optional JSON-LD.
 *
 * Usage:
 *   <Seo
 *     title="LexiNetwork — AI legal intake"
 *     description="Get a free case review."
 *     path="/"
 *     jsonLd={[{...}]}
 *   />
 *
 * Notes:
 *  - `path` is the URL path on lexinetwork.com (e.g. "/sms"). It's combined
 *    with the canonical host so the same component works for preview and
 *    production builds.
 *  - JSON-LD payloads are stringified; pass an array of objects for multiple
 *    blocks (Organization + WebSite + FAQPage on the same page is common).
 */
const SITE = "https://lexinetwork.com";
const DEFAULT_OG = `${SITE}/og-image.png`;

export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_OG,
  noIndex = false,
  jsonLd = null,
}) {
  const url = `${SITE}${path}`;
  const robots = noIndex ? "noindex, nofollow" : "index, follow";
  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="LexiNetwork" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
}
